import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { PageData, ProfileGroup } from 'types/Page';
import { formatURLQuery } from 'utils/url';
import {
  getInitialEnabledGroupPageIds,
  toggleEnabledGroupPageId,
} from 'app/modules/analytics/utils/analyticsGroupProfileSelection';
import {
  ANALYTICS_SELECTION_QUERY_KEYS,
  applyAnalyticsSelectionToQuery,
  resolveAnalyticsSelection,
} from 'app/modules/analytics/utils/analyticsSelectionQuery';
import {
  applyAnalyticsFiltersToQuery,
  readAnalyticsFiltersFromQuery,
} from 'app/modules/analytics/utils/analyticsFiltersQuery';
import {
  AnalyticsFiltersSnapshot,
  differsFromPersistedAnalyticsFilters,
  resolveAnalyticsFilters,
  toSerializedAnalyticsFilters,
} from 'app/modules/analytics/utils/analyticsFiltersSerialization';
import { useAnalyticsFiltersStore } from 'app/modules/analytics/store/analyticsFiltersStore';
import { profileGroupEligibleForAnalyticsPicker } from 'app/modules/analytics/utils/analyticsNonGlobalProfileGroups';
import { useLastUsedAnalyticsProfilesStore } from 'app/modules/analytics/hooks/useLastUsedAnalyticsProfiles';
import {
  useAnalyticsInventory,
  useAnalyticsSessionAuthError,
} from 'app/modules/analytics/hooks/useAnalyticsInventory';

export interface AnalyticsContextValue {
  pages: PageData[];
  profileGroups: ProfileGroup[];
  isLoading: boolean;
  hasSessionAuthError: boolean;
  selectedPage: PageData | null;
  selectedPageId: number | null;
  selectedGroupId: number | null;
  selectedProfileGroup: ProfileGroup | null;
  isGroupSelected: boolean;
  enabledGroupPageIds: ReadonlySet<number>;
  toggleGroupPageId: (pageId: number) => void;
  selectPage: (page: PageData) => void;
  selectGroup: (groupId: number) => void;
  markAwaitingCreatedGroup: () => void;
  markAwaitingCreatedProfile: () => void;
  timezone: string;
}

export const AnalyticsContext = createContext<
  AnalyticsContextValue | undefined
>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
}

function analyticsFiltersKey(filters: AnalyticsFiltersSnapshot): string {
  return JSON.stringify(toSerializedAnalyticsFilters(filters));
}

export const AnalyticsProvider: FC<AnalyticsProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedPageId, setSelectedPageId] = useState<number | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const hasResolvedRef = useRef(false);
  const filtersResetRef = useRef(false);
  const lastWrittenFiltersRef = useRef<string | null>(null);
  const knownGroupIdsRef = useRef<Set<number> | null>(null);
  const isAwaitingCreatedGroupRef = useRef(false);
  const knownPageIdsRef = useRef<Set<number> | null>(null);
  const isAwaitingCreatedPageRef = useRef(false);
  const [persistReady, setPersistReady] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return useLastUsedAnalyticsProfilesStore.persist.hasHydrated();
  });
  const [enabledGroupPageIds, setEnabledGroupPageIds] = useState<
    ReadonlySet<number>
  >(() => new Set());

  // Entering analytics is a clean slate: the store outlives the route, and
  // nothing but the link itself should carry a range into a fresh visit.
  if (!filtersResetRef.current) {
    filtersResetRef.current = true;
    useAnalyticsFiltersStore.getState().reset();
  }

  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  const { pages, profileGroups, isLoading } = useAnalyticsInventory();
  const hasSessionAuthError = useAnalyticsSessionAuthError();

  // Serialised so the effect below depends on the filter values themselves,
  // not on the identity of freshly derived Dayjs instances.
  const filtersQueryKey = useAnalyticsFiltersStore(analyticsFiltersKey);

  const hasUrlSelectionParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (
      params.has(ANALYTICS_SELECTION_QUERY_KEYS.profiles) ||
      params.has(ANALYTICS_SELECTION_QUERY_KEYS.groups)
    );
  }, [location.search]);

  useEffect(() => {
    if (useLastUsedAnalyticsProfilesStore.persist.hasHydrated()) {
      setPersistReady(true);
      return;
    }
    const unsub = useLastUsedAnalyticsProfilesStore.persist.onFinishHydration(
      () => {
        setPersistReady(true);
      },
    );
    return unsub;
  }, []);

  // Writes nothing when the URL already says that, so repeating any of the
  // calls below cannot oscillate.
  const replaceQuery = useCallback(
    (params: URLSearchParams) => {
      const qs = params.toString();
      if (qs === new URLSearchParams(location.search).toString()) {
        return;
      }

      const next = qs ? `?${formatURLQuery(qs)}` : '';
      navigate(`${location.pathname}${next}${location.hash}`, {
        replace: true,
      });
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  const syncSelectionToUrl = useCallback(
    (pageId: number | null, groupId: number | null) => {
      const params = new URLSearchParams(location.search);
      applyAnalyticsSelectionToQuery(params, pageId, groupId);
      replaceQuery(params);
    },
    [location.search, replaceQuery],
  );

  const syncFiltersToUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    applyAnalyticsFiltersToQuery(params, useAnalyticsFiltersStore.getState());
    replaceQuery(params);
  }, [location.search, replaceQuery]);

  useEffect(() => {
    if (isLoading || (!persistReady && !hasUrlSelectionParams)) {
      return;
    }

    const persisted = {
      pageId: useLastUsedAnalyticsProfilesStore.getState().lastPageId,
      groupId: useLastUsedAnalyticsProfilesStore.getState().lastGroupId,
    };
    const resolved = resolveAnalyticsSelection(
      location.search,
      pages,
      profileGroups,
      persisted,
    );

    setSelectedPageId(resolved.pageId);
    setSelectedGroupId(resolved.groupId);

    useLastUsedAnalyticsProfilesStore.getState().setLastSelection({
      pageId: resolved.pageId,
      groupId: resolved.groupId,
    });

    // Filters are read from the URL exactly once, on the way in. Reading them
    // on every URL change is what let the old two-way sync fight the selection
    // writer; from here on the store is the source and the URL follows it.
    if (!hasResolvedRef.current) {
      hasResolvedRef.current = true;

      const urlFilters = readAnalyticsFiltersFromQuery(location.search);
      let recoveredUnsupportedRange = false;

      if (urlFilters) {
        const filters = useAnalyticsFiltersStore.getState();
        const resolved = resolveAnalyticsFilters(urlFilters, filters);

        recoveredUnsupportedRange = differsFromPersistedAnalyticsFilters(
          urlFilters,
          resolved,
        );
        filters.applyResolvedFilters(resolved);
      }

      // Whatever the entry resolved to is the baseline the writer compares
      // against, so landing here never counts as a change — unless the link
      // carried a range we had to replace, in which case the address bar must
      // be corrected to the period actually shown.
      lastWrittenFiltersRef.current = recoveredUnsupportedRange
        ? null
        : analyticsFiltersKey(useAnalyticsFiltersStore.getState());
    }

    syncSelectionToUrl(resolved.pageId, resolved.groupId);
  }, [
    hasUrlSelectionParams,
    isLoading,
    location.hash,
    location.pathname,
    location.search,
    pages,
    persistReady,
    profileGroups,
    syncSelectionToUrl,
  ]);

  // The filter keys are written only once the user moves them. Entering the
  // module adopts whatever the link carried — or nothing at all — instead of
  // stamping the current range over a URL nobody asked to change.
  useEffect(() => {
    if (!hasResolvedRef.current) {
      return;
    }

    // Read live rather than trusting the rendered key: the entry baseline is
    // pinned from inside the effect above, one commit ahead of this render.
    const currentKey = analyticsFiltersKey(useAnalyticsFiltersStore.getState());

    if (lastWrittenFiltersRef.current === currentKey) {
      return;
    }

    lastWrittenFiltersRef.current = currentKey;
    syncFiltersToUrl();
  }, [filtersQueryKey, syncFiltersToUrl]);

  const selectPage = useCallback(
    (page: PageData) => {
      isAwaitingCreatedGroupRef.current = false;
      isAwaitingCreatedPageRef.current = false;
      setSelectedPageId(page.id);
      setSelectedGroupId(null);
      useLastUsedAnalyticsProfilesStore.getState().addProfile({
        id: page.id,
        name: page.name,
        type: page.type,
        logoSrc: page.logo?.src ?? undefined,
      });
      useLastUsedAnalyticsProfilesStore.getState().setLastSelection({
        pageId: page.id,
        groupId: null,
      });
      syncSelectionToUrl(page.id, null);
    },
    [syncSelectionToUrl],
  );

  const selectGroup = useCallback(
    (groupId: number) => {
      isAwaitingCreatedGroupRef.current = false;
      isAwaitingCreatedPageRef.current = false;
      setSelectedGroupId(groupId);
      setSelectedPageId(null);
      useLastUsedAnalyticsProfilesStore.getState().setLastSelection({
        pageId: null,
        groupId,
      });
      syncSelectionToUrl(null, groupId);
    },
    [syncSelectionToUrl],
  );

  const markAwaitingCreatedGroup = useCallback(() => {
    isAwaitingCreatedGroupRef.current = true;
  }, []);

  const markAwaitingCreatedProfile = useCallback(() => {
    isAwaitingCreatedPageRef.current = true;
  }, []);

  const selectedPage = useMemo(
    () => pages.find((p) => p.id === selectedPageId) ?? null,
    [pages, selectedPageId],
  );

  const selectedProfileGroup = useMemo(
    () => profileGroups.find((g) => g.id === selectedGroupId) ?? null,
    [profileGroups, selectedGroupId],
  );

  useEffect(() => {
    if (selectedProfileGroup) {
      setEnabledGroupPageIds(
        getInitialEnabledGroupPageIds(selectedProfileGroup.pages),
      );
    } else {
      setEnabledGroupPageIds(new Set());
    }
  }, [selectedProfileGroup]);

  const toggleGroupPageId = useCallback((pageId: number) => {
    setEnabledGroupPageIds((prev) => toggleEnabledGroupPageId(prev, pageId));
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (selectedGroupId === null) {
      return;
    }
    const group = profileGroups.find((g) => g.id === selectedGroupId);

    if (profileGroupEligibleForAnalyticsPicker(group)) {
      return;
    }

    const fallbackPageId = pages.length > 0 ? pages[0].id : null;
    setSelectedGroupId(null);
    setSelectedPageId(fallbackPageId);
    useLastUsedAnalyticsProfilesStore.getState().setLastSelection({
      pageId: fallbackPageId,
      groupId: null,
    });
    syncSelectionToUrl(fallbackPageId, null);
  }, [isLoading, profileGroups, selectedGroupId, pages, syncSelectionToUrl]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (selectedGroupId !== null) {
      return;
    }
    if (selectedPageId === null) {
      return;
    }
    if (pages.some((p) => p.id === selectedPageId)) {
      return;
    }

    const fallbackPageId = pages.length > 0 ? pages[0].id : null;
    setSelectedPageId(fallbackPageId);
    useLastUsedAnalyticsProfilesStore.getState().setLastSelection({
      pageId: fallbackPageId,
      groupId: null,
    });
    syncSelectionToUrl(fallbackPageId, null);
  }, [isLoading, pages, selectedGroupId, selectedPageId, syncSelectionToUrl]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const currentGroupIds = new Set(profileGroups.map((group) => group.id));

    if (knownGroupIdsRef.current === null) {
      knownGroupIdsRef.current = currentGroupIds;
      return;
    }

    if (isAwaitingCreatedGroupRef.current) {
      const createdGroup = profileGroups
        .filter(
          (group) =>
            !knownGroupIdsRef.current!.has(group.id) &&
            profileGroupEligibleForAnalyticsPicker(group),
        )
        .reduce<ProfileGroup | null>(
          (newest, group) => (newest && newest.id > group.id ? newest : group),
          null,
        );

      if (createdGroup) {
        knownGroupIdsRef.current = currentGroupIds;
        selectGroup(createdGroup.id);
        return;
      }
    }

    knownGroupIdsRef.current = currentGroupIds;
  }, [isLoading, profileGroups, selectGroup]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const currentPageIds = new Set(pages.map((page) => page.id));

    if (knownPageIdsRef.current === null) {
      knownPageIdsRef.current = currentPageIds;
      return;
    }

    if (isAwaitingCreatedPageRef.current) {
      const createdPage = pages
        .filter((page) => !knownPageIdsRef.current!.has(page.id))
        .reduce<PageData | null>(
          (newest, page) => (newest && newest.id > page.id ? newest : page),
          null,
        );

      if (createdPage) {
        knownPageIdsRef.current = currentPageIds;
        selectPage(createdPage);
        return;
      }
    }

    knownPageIdsRef.current = currentPageIds;
  }, [isLoading, pages, selectPage]);

  const isGroupSelected = selectedGroupId !== null;

  const isContextLoading =
    isLoading || (!persistReady && !hasUrlSelectionParams);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      pages,
      profileGroups,
      isLoading: isContextLoading,
      hasSessionAuthError,
      selectedPage,
      selectedPageId,
      selectedGroupId,
      selectedProfileGroup,
      isGroupSelected,
      enabledGroupPageIds,
      toggleGroupPageId,
      selectPage,
      selectGroup,
      markAwaitingCreatedGroup,
      markAwaitingCreatedProfile,
      timezone,
    }),
    [
      pages,
      profileGroups,
      isContextLoading,
      hasSessionAuthError,
      selectedPage,
      selectedPageId,
      selectedGroupId,
      selectedProfileGroup,
      isGroupSelected,
      enabledGroupPageIds,
      toggleGroupPageId,
      selectPage,
      selectGroup,
      markAwaitingCreatedGroup,
      markAwaitingCreatedProfile,
      timezone,
    ],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export function useAnalyticsContext(): AnalyticsContextValue {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    throw new Error(
      'useAnalyticsContext must be used within AnalyticsProvider',
    );
  }
  return ctx;
}
